import React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Chip } from '@material-ui/core';
import clsx from 'clsx';
import CheckIcon from '@material-ui/icons/Check';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Same chip treatment as FollowUpActionChips.tsx/OutcomeSegmentedControl.tsx's own
        // selected state (indigo accent, 26px height) - explicit request for a single boolean
        // toggle in this same chip style, so plain MUI Checkboxes next to it don't look like a
        // different control language on the same page.
        chip: {
            height: 26,
            borderRadius: 999,
            border: '1px solid #CDD4DF',
            backgroundColor: '#fff',
            fontSize: 12.5,
            '& .MuiChip-label': {
                padding: '0 10px',
            },
        },
        chipSelected: {
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            borderColor: 'var(--rv-accent-indigo)',
            color: 'var(--rv-accent-indigo)',
            fontWeight: 700,
            '& $chipCheckIcon': {
                color: 'var(--rv-accent-indigo)',
            },
        },
        chipDisabled: {
            opacity: 0.5,
            pointerEvents: 'none',
        },
        chipCheckIcon: {
            fontSize: 16,
        },
    }),
);

interface ToggleChipProps {
    label: React.ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    // Not passed to the underlying Chip (MUI v4's Chip has no `name` prop) - kept only so
    // callers can label each instance the same way they'd name a checkbox, for their own
    // readability at the call site.
    name?: string;
}

// A boolean on/off control styled as a single clickable Chip (checked = filled indigo + check
// icon, unchecked = plain outlined) instead of a checkbox + label - a drop-in visual swap:
// callers keep whatever onChange side effects their existing Checkbox handler already had,
// this component only decides when to call it and how it looks.
export const ToggleChip: React.FC<ToggleChipProps> = ({ label, checked, onChange, disabled }) => {
    const classes = useStyles();

    return (
        <Chip
            label={label}
            size="small"
            clickable={!disabled}
            variant="outlined"
            icon={checked ? <CheckIcon className={classes.chipCheckIcon} /> : undefined}
            className={clsx(classes.chip, checked && classes.chipSelected, disabled && classes.chipDisabled)}
            onClick={() => {
                if (!disabled) {
                    onChange(!checked);
                }
            }}
        />
    );
};
